import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, chartLink, formatDateTime, formatRelative } from '../api';
import { usePolling } from '../auth';
import NewsCheckLine from '../components/NewsCheckLine';
import { isRuleTrace, RuleChecks } from '../components/RuleTrace';
import {
  ActionBadge,
  Badge,
  Card,
  Empty,
  Notice,
  ScoreMeter,
  Spinner,
} from '../components/ui';
import type { CycleSummary, DashboardResponse, Decision } from '../types';

/**
 * The decision log is where the three approaches become comparable in detail:
 * it records not just the trades but every proposal that was considered and
 * rejected, including which side of the hybrid gate blocked it.
 */
export default function Decisions() {
  const [streamId, setStreamId] = useState<number | ''>('');
  const [action, setAction] = useState<'' | 'BUY' | 'SELL' | 'HOLD'>('');
  const [acceptedOnly, setAcceptedOnly] = useState(false);

  const dashboard = usePolling<DashboardResponse>(() => api.dashboard(), null);
  const decisions = usePolling<{ decisions: Decision[] }>(
    () =>
      api.decisions({
        limit: 150,
        ...(streamId === '' ? {} : { streamId }),
        ...(action === '' ? {} : { action }),
        ...(acceptedOnly ? { accepted: true } : {}),
      }),
    30_000,
    [streamId, action, acceptedOnly],
  );
  const cycles = usePolling<{ cycles: CycleSummary[] }>(() => api.cycles(25), 30_000);

  const rows = decisions.data?.decisions ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Decisions</h1>
          <p>
            Everything each stream considered, acted on or passed over. For the hybrid stream this
            shows both votes, so you can see when the model overruled the pattern engine.
          </p>
        </div>
        <button className="btn" type="button" onClick={decisions.reload}>
          Refresh
        </button>
      </div>

      <Card title="Filters">
        <div className="inline-form">
          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="dec-stream">Stream</label>
            <select
              id="dec-stream"
              value={streamId}
              onChange={(event) => setStreamId(event.target.value === '' ? '' : Number(event.target.value))}
            >
              <option value="">All streams</option>
              {(dashboard.data?.streams ?? []).map((stream) => (
                <option key={stream.id} value={stream.id}>
                  {stream.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ maxWidth: 150 }}>
            <label htmlFor="dec-action">Action</label>
            <select
              id="dec-action"
              value={action}
              onChange={(event) => setAction(event.target.value as '' | 'BUY' | 'SELL' | 'HOLD')}
            >
              <option value="">All</option>
              <option value="BUY">Buy</option>
              <option value="SELL">Sell</option>
              <option value="HOLD">Hold</option>
            </select>
          </div>
          <div className="field" style={{ maxWidth: 190 }}>
            <label htmlFor="dec-accepted">Outcome</label>
            <select
              id="dec-accepted"
              value={acceptedOnly ? 'accepted' : 'all'}
              onChange={(event) => setAcceptedOnly(event.target.value === 'accepted')}
            >
              <option value="all">All decisions</option>
              <option value="accepted">Executed only</option>
            </select>
          </div>
        </div>
      </Card>

      {decisions.error && <Notice tone="error">{decisions.error}</Notice>}

      <Card title="Recent engine cycles" subtitle="including cycles that produced no trades">
        {cycles.data && cycles.data.cycles.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Stream</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th className="num">Scanned</th>
                  <th className="num">Proposed</th>
                  <th className="num">Executed</th>
                  <th className="num">Took</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {cycles.data.cycles.map((cycle) => (
                  <tr key={cycle.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(cycle.startedAt)}</td>
                    <td>{cycle.streamName}</td>
                    <td>
                      <Badge
                        tone={
                          cycle.status === 'ok'
                            ? 'green'
                            : cycle.status === 'error'
                              ? 'red'
                              : cycle.status === 'running'
                                ? 'blue'
                                : 'grey'
                        }
                      >
                        {cycle.status}
                      </Badge>
                    </td>
                    <td className="mono-small">{cycle.trigger}</td>
                    <td className="num">{cycle.symbolsScanned}</td>
                    <td className="num">{cycle.ordersProposed}</td>
                    <td className="num">{cycle.ordersExecuted}</td>
                    <td className="num">{cycle.durationMs === null ? '—' : `${cycle.durationMs}ms`}</td>
                    <td className="reason" style={{ maxWidth: '46ch' }}>
                      {cycle.error ?? cycle.note ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No cycles recorded yet.</Empty>
        )}
      </Card>

      <Card title="Decision feed" subtitle={`${rows.length} most recent`}>
        {decisions.loading && rows.length === 0 ? (
          <Spinner label="Loading decisions" />
        ) : rows.length === 0 ? (
          <Empty>No decisions recorded yet.</Empty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((decision) => (
              <DecisionRow key={decision.id} decision={decision} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function DecisionRow({ decision }: { decision: Decision }) {
  // patternVotes and aiVotes are decoded from persisted JSON, so treat every
  // field as untrusted: only render numeric ones through toFixed.
  const rawScore = decision.patternVotes?.score;
  const patternScore = typeof rawScore === 'number' ? rawScore : undefined;
  const rsi = decision.patternVotes?.rsi14;
  const aiVerdict = decision.aiVotes?.verdict ?? decision.aiVotes?.action;
  // Momentum decisions carry a rank instead of a pattern score. Guarded the same
  // way, since it is decoded from persisted JSON.
  const mom = decision.patternVotes?.momentum ?? undefined;
  // Options decisions carry the contract and its model quote.
  const opt = decision.patternVotes?.option ?? undefined;
  // The news check, when the instrument had news (or the check could not run).
  const news = decision.newsCheck && decision.newsCheck.verdict !== 'NO_NEWS' ? decision.newsCheck : undefined;

  return (
    <div
      style={{
        border: '1px solid var(--hair)',
        padding: '10px 12px',
        background: 'var(--bg)',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <ActionBadge action={decision.action} />
        <Link className="sym" to={chartLink(decision.symbol, { decision: decision.id })} title="Open this decision on the chart">
          {decision.symbol}
        </Link>
        <span style={{ fontSize: 12, color: 'var(--ink-dim)' }}>{decision.name}</span>
        <Badge tone="grey">{decision.streamName}</Badge>
        {decision.accepted ? (
          <Badge tone="green">Executed</Badge>
        ) : decision.action === 'HOLD' ? (
          <Badge tone="grey">No action</Badge>
        ) : (
          <Badge tone="amber">Not executed</Badge>
        )}
        {aiVerdict && (
          <Badge tone={aiVerdict === 'CONFIRM' ? 'green' : aiVerdict === 'VETO' ? 'red' : 'blue'}>
            model: {aiVerdict}
          </Badge>
        )}
        {decision.confidence !== null && (
          <span className="mono-small">confidence {decision.confidence.toFixed(2)}</span>
        )}
        <span className="mono-small" style={{ marginLeft: 'auto' }}>
          {formatRelative(decision.createdAt)}
        </span>
      </div>

      {patternScore !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 7 }}>
          <span className="mono-small" style={{ minWidth: 108 }}>
            pattern {patternScore >= 0 ? '+' : ''}
            {patternScore.toFixed(3)}
          </span>
          <ScoreMeter score={patternScore} />
          {decision.patternVotes?.trend && (
            <span className="mono-small">trend {decision.patternVotes.trend}</span>
          )}
          {typeof rsi === 'number' && <span className="mono-small">RSI {rsi.toFixed(1)}</span>}
        </div>
      )}

      {mom && typeof mom.rank === 'number' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 7, flexWrap: 'wrap' }}>
          <span className="mono-small">
            rank {mom.rank}
            {typeof mom.of === 'number' ? ` / ${mom.of}` : ''}
            {mom.label ? ` (${mom.label})` : ''}
          </span>
          {typeof mom.momentumPct === 'number' && (
            <span className="mono-small">
              momentum {mom.momentumPct >= 0 ? '+' : ''}
              {mom.momentumPct.toFixed(1)}%
            </span>
          )}
          {typeof mom.pctFrom52WeekHigh === 'number' && (
            <span className="mono-small">{Math.abs(mom.pctFrom52WeekHigh).toFixed(1)}% off high</span>
          )}
          {decision.patternVotes?.trend && (
            <span className="mono-small">trend {decision.patternVotes.trend}</span>
          )}
        </div>
      )}

      {opt && typeof opt.strike === 'number' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 7, flexWrap: 'wrap' }}>
          <span className="mono-small">
            {opt.underlying ?? ''} {opt.strike} {opt.right ?? ''}
            {opt.expiry ? ` · ${opt.expiry}` : ''}
            {typeof opt.days === 'number' ? ` (${opt.days.toFixed(0)}d)` : ''}
          </span>
          {typeof opt.delta === 'number' && <span className="mono-small">Δ {opt.delta.toFixed(2)}</span>}
          {typeof opt.iv === 'number' && <span className="mono-small">IV {opt.iv.toFixed(1)}%</span>}
          {typeof opt.bid === 'number' && typeof opt.ask === 'number' && (
            <span className="mono-small">
              {opt.bid.toFixed(2)} / {opt.ask.toFixed(2)}
            </span>
          )}
          {typeof opt.contracts === 'number' && (
            <span className="mono-small">
              {opt.contracts} contract{opt.contracts === 1 ? '' : 's'}
              {opt.fractional && opt.contracts < 1 ? ' (fractional)' : ''}
            </span>
          )}
          <Badge tone="grey">model price</Badge>
        </div>
      )}

      <RuleLine decision={decision} />

      {news && <NewsCheckLine check={news} />}

      {decision.patternVotes?.patterns && decision.patternVotes.patterns.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
          {decision.patternVotes.patterns.slice(0, 6).map((pattern, index) => (
            <Badge
              // pattern.votes is decoded from JSON persisted before the current
              // shape was enforced, so name/strength may be missing on old rows.
              key={`${pattern.name ?? 'pattern'}-${index}`}
              tone={
                pattern.direction === 'bullish' ? 'green' : pattern.direction === 'bearish' ? 'red' : 'grey'
              }
            >
              {pattern.name ?? 'pattern'}
              {typeof pattern.strength === 'number' ? ` ${pattern.strength.toFixed(2)}` : ''}
            </Badge>
          ))}
        </div>
      )}

      {decision.rationale && (
        <p className="reason" style={{ margin: '7px 0 0' }}>
          {decision.rationale}
        </p>
      )}

      {decision.rejectReason && (
        <p className="reason" style={{ margin: '5px 0 0', color: 'var(--amber)' }}>
          Blocked: {decision.rejectReason}
        </p>
      )}
    </div>
  );
}

/**
 * The rule behind a rule-based decision, in one line (the checks that decided
 * it), with the full set of checks a click away and the chart a link away.
 */
function RuleLine({ decision }: { decision: Decision }) {
  const trace = decision.ruleTrace;
  if (!isRuleTrace(trace)) return null;
  const checks = trace.checks ?? [];
  const decisive = checks.filter((check) => check.decisive);
  return (
    <div className="trace-line">
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="trace-line-head">{trace.headline}</span>
        <Link className="mono-small" to={chartLink(decision.symbol, { decision: decision.id })}>
          view on chart →
        </Link>
      </div>
      {decisive.length > 0 && (
        <div className="mono-small" style={{ marginTop: 3 }}>
          {decisive.map((check, index) => (
            <span key={`${check.rule}-${index}`}>
              {index > 0 && ' · '}
              {check.rule}: {check.value} (needed {check.test}) {check.passed ? '✓' : '✗'}
            </span>
          ))}
        </div>
      )}
      {checks.length > decisive.length && (
        <details className="fold" style={{ marginTop: 6 }}>
          <summary>All {checks.length} rule checks</summary>
          <RuleChecks checks={checks} compact />
        </details>
      )}
    </div>
  );
}
